import { useCallback, useMemo } from 'react'

import type { Filter, TimeDimension } from '@cubejs-client/core'
import {
  QueryOptionsWithSearch,
  SiteConciergeSearchCountQuery,
  SiteConciergeSearchTableQuery,
  useCubeReportPageFiltersContext,
} from 'cube'
import {
  DRIFT_SITE_CONCIERGE_DIMENSIONS,
  DRIFT_SITE_CONCIERGE_SEARCH_DIMENSIONS,
  DRIFT_SITE_CONCIERGE_SEARCH_MEASURES,
} from 'cube/constants'
import moment from 'moment'
import * as R from 'ramda'

import { useTableState } from '@driftt/dds'
import {
  CubeQueryBuilder,
  resultSetAsTableData,
  useCubeQuery,
} from '@driftt/dds.reports'

import { resolveCubeKPIResults } from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'
import { isNilOrEmpty } from 'utils/ramda'

type SiteConciergeSearchTableMetricsRow = {
  messageId: string
  query: string
  isAiGenerated: boolean
  queryResults: string
  visitor: string
  company: string
  date: string
}

export const useSiteConciergeSearchTable = (
  options?: QueryOptionsWithSearch
) => {
  const { searchFilter } = { ...options }
  const { pageFilters = [] } = useCubeReportPageFiltersContext()

  const timePeriod = useTimePeriod()
  const dateRange: [string, string] = [
    moment(timePeriod.startDate).format('YYYY-MM-DD'),
    moment(timePeriod.endDate).format('YYYY-MM-DD'),
  ]

  const timeDimensions: TimeDimension[] = [
    {
      dimension: DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT,
      dateRange,
    },
  ]

  const filters: Filter[] = [
    {
      member: DRIFT_SITE_CONCIERGE_DIMENSIONS.ACTION,
      operator: 'equals',
      values: ['sendMessage'],
    },
    {
      member: DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_NAME,
      operator: 'equals',
      values: ['SearchApp'],
    },
    ...pageFilters,
  ]

  if (!isNilOrEmpty(searchFilter)) {
    filters.push({
      or: [
        {
          member: DRIFT_SITE_CONCIERGE_SEARCH_DIMENSIONS.SEARCH_QUERY,
          operator: 'contains',
          values: [searchFilter],
        },
        {
          member: DRIFT_SITE_CONCIERGE_SEARCH_DIMENSIONS.SEARCH_RESULT,
          operator: 'contains',
          values: [searchFilter],
        },
      ],
    })
  }

  const { tableState, handleTableStateChange } =
    useTableState<SiteConciergeSearchTableMetricsRow>({
      defaultPagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      defaultSort: [{ id: 'date', desc: true }],
    })

  const sortOder = R.pathOr('desc', ['sorting', 0, 'desc'], tableState)

  const handleUpdateTableState = useCallback(
    (newState) => {
      handleTableStateChange(newState)
    },
    [handleTableStateChange]
  )

  const query = new CubeQueryBuilder({
    query: SiteConciergeSearchTableQuery,
  })
    .withName(
      `SiteConciergeSearchQuery-${tableState.pagination.pageIndex}-${tableState.pagination.pageSize}`
    )
    .withTimeDimensions(timeDimensions)
    .withFilters(filters)
    .withOrder([
      [
        DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT_WITH_ZONE,
        sortOder ? 'desc' : 'asc',
      ],
    ])
    .withLimit(tableState.pagination.pageSize)
    .withOffset(
      tableState.pagination.pageIndex * tableState.pagination.pageSize
    )
    .build()

  const { data, isLoading, isError } = useCubeQuery({
    query,
  })

  const tableData = useMemo(() => {
    const resultsAsTableData = resultSetAsTableData(data)
    return resultsAsTableData?.map(
      ({ DriftSiteConcierge, DriftChatEndUsers }) => ({
        messageId: DriftSiteConcierge.searchMessageId,
        query: DriftSiteConcierge.searchQuery,
        isAiGenerated: DriftSiteConcierge.searchIsGenerated,
        queryResults: DriftSiteConcierge.searchResult,
        visitor: DriftChatEndUsers.displayName,
        company: DriftChatEndUsers.companyName || '--',
        date: moment(DriftSiteConcierge.eventSentAtWithZone as string).format(
          'MM/DD/YYYY hh:mm:ss A'
        ),
      })
    ) as SiteConciergeSearchTableMetricsRow[]
  }, [data])

  const searchPerformedCountQuery = new CubeQueryBuilder({
    query: SiteConciergeSearchCountQuery,
  })
    .withTimeDimensions(timeDimensions)
    .withFilters(filters)
    .build()

  const {
    data: searchPerformedResultSet,
    isLoading: isLoadingSearchPerformedCount,
    isError: isErrorSearchPerformedCount,
  } = useCubeQuery({
    query: searchPerformedCountQuery,
  })

  const totalRowCount = useMemo(
    () =>
      resolveCubeKPIResults(
        searchPerformedResultSet,
        DRIFT_SITE_CONCIERGE_SEARCH_MEASURES.COUNT_SEARCHES_PERFORMED
      ),
    [searchPerformedResultSet]
  )

  return useMemo(
    () => ({
      tableData,
      totalRowCount,
      isLoading: isLoading || isLoadingSearchPerformedCount,
      isError: isError || isErrorSearchPerformedCount,
      pagination: tableState.pagination,
      handleUpdateTableState,
    }),
    [
      tableData,
      totalRowCount,
      isLoading,
      isLoadingSearchPerformedCount,
      isError,
      isErrorSearchPerformedCount,
      tableState.pagination,
      handleUpdateTableState,
    ]
  )
}
